import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import { Formik } from 'formik'
import { pipe, mergeLeft, pickAll, evolve, equals, complement } from 'ramda'
import * as Yup from 'yup'
import { breakpoints, colors } from '@/styles'
import { defaultIfFalsy } from '@/utils'
import TenancyDetailsIcon from './tenancy-detail-icon.svg'
import RentDetailsIcon from './rent-detail-icon.svg'
import FormContainer from './form-container'

const Container = styled.div`
  max-width: 1192px;
  margin: 34px 30px 80px;
`

const HeadingContainer = styled.div`
  display: flex;
  margin-bottom: 40px;
`

const Heading = styled.h5`
  margin-left: 10px;
  font-size: 20px;
  font-weight: bold;

  @media screen and (max-width: ${breakpoints.ipadMiniMax}) {
    margin-left: 0;
  }
`

const GridContainer = styled.div`
  display: grid;
  grid-template-columns: 50px 3fr;
  width: 100%;

  @media screen and (max-width: ${breakpoints.ipadMiniMax}) {
    display: block;
  }
`

const GridOne = styled.div`
  grid-column-start: 1;

  @media screen and (max-width: ${breakpoints.ipadMiniMax}) {
    display: none;
  }
`

const GridTwo = styled.div`
  grid-column-start: 2;
  max-width: 760px;
`

const VerticalLine = styled.span`
  display: block;
  width: 38px;
  height: 135px;
  margin-top: -10px;
  background: linear-gradient(${colors.grey88}, ${colors.grey88}) no-repeat center/2px 100%;
`
const doesNotEqual = complement(equals)
const validationSchema = Yup.object().shape({
  lease_type: Yup.string().required(),
  start_date: Yup.string().required(),
  end_date: Yup.mixed().when('lease_type', {
    is: 'fixed',
    then: Yup.date().required(),
    otherwise: null
  }),
  price: Yup.number().required(),
  price_frequency: Yup.string().required()
})

const LeaseTerms = ({ handleValidate, currentStep, contract, setLeaseTerms }) => {
  const { t } = useTranslation()
  const initialValueSource = contract.lease_type ? contract : contract.listing

  const initialValues = pipe(
    pickAll(['lease_type', 'start_date', 'end_date', 'price', 'price_frequency']),
    evolve({
      lease_type: defaultIfFalsy(''),
      start_date: defaultIfFalsy(''),
      end_date: defaultIfFalsy(''),
      price: defaultIfFalsy(''),
      price_frequency: defaultIfFalsy('')
    }),
    mergeLeft({
      price: initialValueSource?.price ? parseInt(initialValueSource.price) : ''
    })
  )(initialValueSource || {})

  const validateLeaseTerms = async values => {
    try {
      const isFormDirty = doesNotEqual(values, initialValues)
      const result = await validationSchema.validate(values)
      setLeaseTerms({ ...values, isFormDirty })

      handleValidate(!!result)
    } catch (err) {
      handleValidate(false)
    }
  }

  return (
    <Container>
      <HeadingContainer>
        <Heading>{t('l.contract_edit.lease_terms.lease_terms.heading', 'Lease Terms')}</Heading>
      </HeadingContainer>
      <GridContainer>
        <GridOne>
          <img src={TenancyDetailsIcon} alt="icon" />
          <VerticalLine />
          <img src={RentDetailsIcon} alt="icon" />
        </GridOne>
        <GridTwo>
          <Formik
            validate={validateLeaseTerms}
            initialValues={initialValues}
            validationSchema={validationSchema}
            validateOnChange
            validateOnMount
          >
            {({ values, setFieldValue, setFieldTouched, validateForm }) => {
              return (
                <FormContainer
                  values={values}
                  setFieldValue={setFieldValue}
                  setFieldTouched={setFieldTouched}
                  validateForm={validateForm}
                  currentStep={currentStep}
                />
              )
            }}
          </Formik>
        </GridTwo>
      </GridContainer>
    </Container>
  )
}

export default LeaseTerms
